import React, { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { getTestsCount } from '../utils/testUtils';
import "./TestCoverageTable.css";

const TestCoverageTable = ({ nodes }) => {
  const { t } = useTranslation();
  // States for pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [testFilter, setTestFilter] = useState('all'); // 'all', 'with-tests', 'without-tests'

  // Filter nodes according to selected filter
  const filteredNodes = useMemo(() => {
    switch (testFilter) {
      case 'with-tests':
        return nodes.filter(node => getTestsCount(node.testsJson) > 0);
      case 'without-tests':
        return nodes.filter(node => getTestsCount(node.testsJson) === 0);
      default:
        return nodes;
    }
  }, [nodes, testFilter]);

  const totalNodes = nodes.length;
  const filteredTotalNodes = filteredNodes.length;
  const nodesWithTests = nodes.filter(
    (node) => getTestsCount(node.testsJson) > 0
  ).length;
  const coverage =
    totalNodes > 0 ? Math.round((nodesWithTests / totalNodes) * 100) : 0;

  // Pagination calculations based on filtered nodes
  const totalPages = Math.ceil(filteredTotalNodes / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredTotalNodes);
  
  // Nodes to display on current page (filtered)
  const currentNodes = useMemo(() => {
    return filteredNodes.slice(startIndex, endIndex);
  }, [filteredNodes, startIndex, endIndex]);

  // Navigation
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  // Change number of items per page
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Return to first page
  };

  // Change filter
  const handleFilterChange = (newFilter) => {
    setTestFilter(newFilter);
    setCurrentPage(1); // Return to first page when filter changes
  };

  // Get global index of a node (in the complete unfiltered list)
  const getGlobalIndex = (node) => {
    return nodes.findIndex(n => n.name === node.name) + 1;
  };

  // Generate visible page numbers
  const getVisiblePages = () => {
    const delta = 2; // Number of pages to display on each side
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const getCoverageColor = (coverage) => {
    if (coverage >= 80) return "#10b981"; // emerald-500
    if (coverage >= 50) return "#f59e0b"; // amber-500
    return "#ef4444"; // red-500
  };

  const getCoverageStatus = (coverage) => {
    if (coverage >= 80) return t('testCoverageTable.excellent');
    if (coverage >= 60) return t('testCoverageTable.good');
    if (coverage >= 40) return t('testCoverageTable.average');
    return t('testCoverageTable.insufficient');
  };

  const getCoverageIcon = (coverage) => {
    if (coverage >= 80) return "🎯";
    if (coverage >= 60) return "✅";
    if (coverage >= 40) return "⚠️";
    return "❌";
  };

  return (
    <div className="test-coverage-container">
      {/* Header avec titre moderne */}
      <div className="coverage-header">
        <h3 className="coverage-title">
          <span className="title-icon">📊</span>
          {t('testCoverageTable.testCoverage')}
        </h3>
        <div className="coverage-meta">
          <span className="meta-text">
            {t('testCoverage.analysisOf', { 
              count: totalNodes, 
              type: totalNodes > 1 ? t('testCoverage.nodes') : t('testCoverage.node') 
            })}
            {testFilter !== 'all' && (
              <span className="filter-indicator">
                {' '}• {t('testCoverage.displayed', { 
                  count: filteredTotalNodes, 
                  type: filteredTotalNodes > 1 ? t('testCoverage.nodes') : t('testCoverage.node') 
                })}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Carte de statistiques principale */}
      <div className="coverage-stats-card">
        <div className="stats-main">
          <div className="coverage-percentage">
            <span className="percentage-icon">{getCoverageIcon(coverage)}</span>
            <span 
              className="percentage-value"
              style={{ color: getCoverageColor(coverage) }}
            >
              {coverage}%
            </span>
            <span className="coverage-status">
              {getCoverageStatus(coverage)}
            </span>
          </div>
          
          <div className="coverage-details">
            <div className="detail-item">
              <span className="detail-label">{t('testCoverageTable.testedNodes')}:</span>
              <span className="detail-value tested">{nodesWithTests}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('testCoverageTable.nodesWithoutTests')}:</span>
              <span className="detail-value untested">{totalNodes - nodesWithTests}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('testCoverageTable.total')}:</span>
              <span className="detail-value total">{totalNodes}</span>
            </div>
          </div>
        </div>

        {/* Barre de progression moderne */}
        <div className="coverage-progress-container">
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar"
              style={{
                width: `${coverage}%`,
                backgroundColor: getCoverageColor(coverage),
              }}
            >
              <div className="progress-shine"></div>
            </div>
          </div>
          <div className="progress-labels">
            <span className="progress-label start">0%</span>
            <span className="progress-label middle">50%</span>
            <span className="progress-label end">100%</span>
          </div>
        </div>
      </div>

      {/* Tableau détaillé amélioré */}
      <div className="coverage-table-container">
        <div className="table-header">
          <h4 className="table-title">
            <span className="table-icon">📋</span>
            {t('testCoverageTable.detailByNode')}
          </h4>
          <div className="table-controls">
            <div className="pagination-info">
              <span className="pagination-text">
                {t('testCoverageTable.displaying')} {startIndex + 1}-{endIndex} {t('testCoverageTable.of')} {filteredTotalNodes} {filteredTotalNodes > 1 ? t('testCoverage.nodes') : t('testCoverage.node')}
                {testFilter !== 'all' && (
                  <span className="filter-info"> ({t('testCoverageTable.filtered')})</span>
                )}
              </span>
            </div>
            <div className="filter-selector">
              <label htmlFor="testFilter">{t('testCoverageTable.filter')}:</label>
              <select
                id="testFilter"
                value={testFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('testCoverageTable.allNodes')}</option>
                <option value="with-tests">✅ {t('testCoverageTable.withTests')} ({nodesWithTests})</option>
                <option value="without-tests">❌ {t('testCoverageTable.withoutTests')} ({totalNodes - nodesWithTests})</option>
              </select>
            </div>
            <div className="items-per-page-selector">
              <label htmlFor="itemsPerPage">{t('testCoverageTable.perPage')}:</label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="items-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          <div className="table-legend">
            <div className="legend-item">
              <span className="legend-dot has-tests"></span>
              <span>{t('testCoverageTable.withTests')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot no-tests"></span>
              <span>{t('testCoverageTable.withoutTests')}</span>
            </div>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="enhanced-coverage-table">
            <thead>
              <tr>
                <th className="col-node">
                  <span className="th-content">
                    <span className="th-icon">🔗</span>
                    {t('testCoverageTable.node')}
                  </span>
                </th>
                <th className="col-description">
                  <span className="th-content">
                    <span className="th-icon">📝</span>
                    {t('testCoverageTable.description')}
                  </span>
                </th>
                <th className="col-tests">
                  <span className="th-content">
                    <span className="th-icon">🧪</span>
                    {t('testCoverageTable.tests')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {currentNodes.map((node, index) => {
                const testCount = getTestsCount(node.testsJson);
                const hasTests = testCount > 0;
                const globalIndex = getGlobalIndex(node); // Global index in complete list
                
                return (
                  <tr key={node.name} className={hasTests ? "has-tests-row" : "no-tests-row"}>
                    <td className="node-cell">
                      <div className="node-info">
                        <span className="node-name">{node.name}</span>
                        <span className="node-index">#{globalIndex}</span>
                      </div>
                    </td>
                    <td className="description-cell">
                      <div className="description-content">
                        {node.description || t('testCoverageTable.noDescription')}
                      </div>
                    </td>
                    <td className="tests-cell">
                      {hasTests ? (
                        <div className="test-badge has-tests">
                          <span className="test-icon">✅</span>
                          <span className="test-count">{testCount}</span>
                          <span className="test-text">{testCount > 1 ? t('testCoverageTable.testsPlural') : t('testCoverageTable.testSingular')}</span>
                        </div>
                      ) : (
                        <div className="test-badge no-tests">
                          <span className="test-icon">❌</span>
                          <span className="test-text">{t('testCoverageTable.noTest')}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-controls">
              <button
                className="pagination-btn prev"
                onClick={goToPrevious}
                disabled={currentPage === 1}
                title={t('testCoverageTable.previousPage')}
              >
                <span className="btn-icon">⬅️</span>
                <span className="btn-text">{t('testCoverageTable.previous')}</span>
              </button>

              <div className="pagination-numbers">
                {getVisiblePages().map((page, index) => (
                  <button
                    key={index}
                    className={`pagination-number ${
                      page === currentPage ? 'active' : ''
                    } ${page === '...' ? 'dots' : ''}`}
                    onClick={() => typeof page === 'number' && goToPage(page)}
                    disabled={page === '...'}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn next"
                onClick={goToNext}
                disabled={currentPage === totalPages}
                title={t('testCoverageTable.nextPage')}
              >
                <span className="btn-text">{t('testCoverageTable.next')}</span>
                <span className="btn-icon">➡️</span>
              </button>
            </div>

            <div className="pagination-summary">
              <span className="summary-text">
                {t('testCoverageTable.pageOf', { current: currentPage, total: totalPages })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestCoverageTable;